import { composeExpertName } from '@/lib/composeExpertName';
import React, { memo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageButton } from '@/components/MessageButton';
import { countries } from '@/data/countries';
import { capitalizeWords, decodeHtmlEntities, sanitizeBio, cn } from '@/lib/utils';
import { truncateByGraphemes } from '@/utils/languageUtils';
import { useAuth } from '@/contexts/AuthContext';
import { LComponents } from '@/components/i18n';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { useLocalizations } from '@/hooks/useLocalizations';
import { RESPONSIVE, TYPOGRAPHY } from '@/design-system';
import { ORPHAN_SENTINEL_UUID } from '@/constants/config';
import { useLocationById } from '@/hooks/useLocationById';
import { useCountryName } from '@/hooks/useCountryName';

interface UserCardHorizontalProps {
  expert: {
    id: string;
    display_name?: string | null;
    name?: string | null;
    lastname?: string | null;
    business?: string | null;
    image?: string | null;
    avatar_url?: string | null;
    about?: string | null;
    city?: string | null;
    country?: string | null;
    location_id?: number | null;
    vanity_url?: string | null;
    membership_tier?: string | null;
  };
  showActions?: boolean;
  maxBioLength?: number;
  localizedBio?: string;
  openInNewTab?: boolean;
}

/**
 * Horizontal UserCard — wider, more prominent layout for Professional / Business tier users.
 * Spans 2 grid columns on desktop for extra visibility.
 */
export const UserCardHorizontal = memo(function UserCardHorizontal({
  expert,
  showActions = true,
  maxBioLength = 600,
  localizedBio,
  openInNewTab,
}: UserCardHorizontalProps) {
  const { user } = useAuth();
  const { lCommon } = useLocalizations();
  const { trackProfileImpression, trackProfileClick } = useAnalyticsTracking();
  const hasTrackedImpression = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track impression only when card is ≥50% visible (IntersectionObserver)
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !expert.id || expert.id === ORPHAN_SENTINEL_UUID) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedImpression.current) {
          hasTrackedImpression.current = true;
          trackProfileImpression(expert.id);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [expert.id, trackProfileImpression]);

  if (expert.id === ORPHAN_SENTINEL_UUID) return null;

  const displayBioRaw = localizedBio ?? expert.about;
  // location_id is the SOURCE OF TRUTH (per project memory). Legacy `country` is fallback.
  const { isoAlpha2: resolvedIso, name: resolvedName } = useLocationById(expert.location_id);
  const effectiveCountryCode = resolvedIso || expert.country || null;
  const countryInfo = countries.find(c => c.code === effectiveCountryCode);
  // Localized country name in the visitor's language (e.g. "Brasil" on pt pages)
  const localizedCountryName = useCountryName(effectiveCountryCode);
  const countryDisplayName = localizedCountryName || resolvedName || expert.country || null;
  const userImage = expert.image || expert.avatar_url;
  const rawUserName = composeExpertName(expert.display_name, expert.lastname, expert.name) ||
    expert.business ||
    lCommon('labels.expert', { defaultValue: 'Expert' });
  const userName = capitalizeWords(decodeHtmlEntities(rawUserName));
  const profileUrl = expert.vanity_url ? `/${expert.vanity_url}` : `/profile/${expert.id}`;
  const displayBio = displayBioRaw && maxBioLength
    ? truncateByGraphemes(displayBioRaw, maxBioLength, '…')
    : displayBioRaw;

  const locationText = expert.city && countryDisplayName
    ? `${expert.city}, ${countryDisplayName}`
    : countryDisplayName || null;

  const tierLabel = expert.membership_tier === 'business' ? 'Business' : 'Pro';

  const cardContent = (
    <Card ref={cardRef} className="overflow-hidden transition-all duration-300 ease-out relative group hover:shadow-lg hover:scale-[1.005] border-accent/30 h-full">
      <CardContent className={`${RESPONSIVE.paddingCardContentWide} h-full`}>
        {/* Horizontal layout: avatar left, content right */}
        <div className="flex items-start gap-5">
          {/* Larger avatar */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-muted flex-shrink-0 ring-2 ring-accent/20">
            {userImage ? (
              <img
                src={userImage}
                alt={userName}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={cn(
                TYPOGRAPHY.metricValue,
                'w-full h-full items-center justify-center text-muted-foreground',
                userImage ? 'hidden' : 'flex'
              )}
            >
              {userName[0]?.toUpperCase() || '?'}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-lg sm:text-xl leading-tight truncate transition-colors duration-200 group-hover:text-primary flex items-center gap-1">
                    <span className="truncate">{userName}</span>
                    {expert.membership_tier != null && (
                      <VerifiedBadge size={20} displayName={userName} membershipTier={expert.membership_tier} />
                    )}
                  </h3>
                  <Badge variant="default" className="text-xs gap-1 flex-shrink-0">
                    <Star className="h-3 w-3" />
                    {tierLabel}
                  </Badge>
                </div>
                {expert.business && (
                  <p className={cn(TYPOGRAPHY.cardDescription, 'mt-0.5 truncate font-medium')}>
                    {expert.business}
                  </p>
                )}
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-base">{countryInfo?.flag || '🌍'}</span>
                  <span className={cn(TYPOGRAPHY.cardDescription, 'truncate')}>
                    {locationText || <LComponents id="expert_card.location_not_specified" defaultValue='Location Not Specified' />}
                  </span>
                </div>
              </div>

              {showActions && (
                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  <MessageButton
                    userId={expert.id}
                    userName={userName}
                    userAvatar={userImage || undefined}
                    userLocationId={expert.location_id || undefined}
                    size="sm"
                    className="h-7 w-7"
                  />
                </div>
              )}
            </div>

            {/* Bio — more visible in horizontal layout */}
            {displayBio && (
              <p className={cn(TYPOGRAPHY.cardDescription, 'line-clamp-3 sm:line-clamp-4')}>
                {sanitizeBio(displayBio)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Link to={profileUrl} onClick={() => trackProfileClick(expert.id)} className="block" target={openInNewTab ? "_blank" : undefined} rel={openInNewTab ? "noopener noreferrer" : undefined}>
      {cardContent}
    </Link>
  );
});
